import React, { useEffect, useState } from "react";
import {
  Button,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  useToast,
  Box,
  VStack,
  HStack,
  Icon,
  InputGroup,
  InputLeftElement,
  Fade,
} from "@chakra-ui/react";
import { FiUser, FiPhone, FiMapPin, FiHome } from "react-icons/fi";
import client from "../../../helpers/Client";
import CitySelect from "../../../components/select/CitySelect";
import { useRevalidator } from "react-router";

// Phone number formatting function
const formatPhoneNumber = (value) => {
  if (!value) return '';
  
  // Sadece rakamları al
  const numbers = value.replace(/\D/g, '');
  
  // 10 haneli sınır
  const limited = numbers.slice(0, 10);
  
  // Formatla: 5XX XXX XX XX
  if (limited.length >= 7) {
    return `${limited.slice(0, 3)} ${limited.slice(3, 6)} ${limited.slice(6, 8)} ${limited.slice(8, 10)}`;
  } else if (limited.length >= 4) {
    return `${limited.slice(0, 3)} ${limited.slice(3, 6)} ${limited.slice(6)}`;
  } else if (limited.length >= 1) {
    return `${limited.slice(0, 3)} ${limited.slice(3)}`;
  }
  return limited;
};

export default function CreateCustomerModal({ disclosure }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [city, setCity] = useState("");
  const [addres, setAddres] = useState("");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const revalidator = useRevalidator();
  const toast = useToast();

  // Color mode values
  const bgColor = 'white';
  const borderColor = 'gray.200';
  const inputBg = 'gray.50';
  const inputFocusBg = 'white';
  const textColor = 'gray.800';
  const placeholderColor = 'gray.500';

  useEffect(() => {
    if (disclosure.isOpen) {
      setFirstName("");
      setLastName("");
      setPhoneNumber("");
      setErrors({});
      setTouched({});
      setCity("");
      setAddres("");
    }
  }, [disclosure.isOpen, disclosure.onClose]);

  const handleFieldChange = (fieldName, value) => {
    if (fieldName === 'phoneNumber') {
      const formatted = formatPhoneNumber(value);
      setPhoneNumber(formatted);
    }
  };

  const validateField = (fieldName, value) => {
    const newErrors = { ...errors };
    
    if (fieldName === 'phoneNumber') {
      if (!value || value.trim() === '') {
        newErrors.phoneNumber = 'Telefon numarası gereklidir';
      } else {
        const cleanValue = value.replace(/\s/g, '');
        if (cleanValue.length !== 10) {
          newErrors.phoneNumber = 'Telefon numarası 10 haneli olmalıdır';
        } else if (!cleanValue.startsWith('5')) {
          newErrors.phoneNumber = 'Telefon numarası 5 ile başlamalıdır';
        } else {
          delete newErrors.phoneNumber;
        }
      }
    }
    
    setErrors(newErrors);
    return !newErrors[fieldName];
  };

  const handleFieldBlur = (fieldName, value) => {
    setTouched({ ...touched, [fieldName]: true });
    validateField(fieldName, value);
  };

  const handleCreate = () => {
    setIsLoading(true);
    setErrors({});
    
    const cleanedPhoneNumber = phoneNumber.replace(/\s/g, '');
    const requestBody = {
      first_name: firstName,
      last_name: lastName,
      address: addres,
      city: city,
      phone_number: "+90" + cleanedPhoneNumber,
    };
    
    client
      .post(`/customers/`, requestBody)
      .then(() => {
        disclosure.onClose();
        revalidator.revalidate();
        toast({
          title: "Müşteri başarıyla eklendi",
          description: "Yeni müşteri kaydı oluşturuldu.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      })
      .catch((error) => {
        setErrors(error.response?.data || {});
        toast({
          title: "İşlem gerçekleştirilemiyor",
          description: "Lütfen bilgileri kontrol edip tekrar deneyin.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Modal isOpen={disclosure.isOpen} onClose={disclosure.onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Müşteri Ekle</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <FormControl>
            <FormLabel>İsim</FormLabel>
            <Input
              bg={"gray.100"}
              border={0}
              color={"gray.500"}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            {errors.first_name &&
              errors.first_name.map((e) => (
                <Text key={e} color="red">
                  {e}
                </Text>
              ))}
          </FormControl>
          <FormControl>
            <FormLabel>Soyad</FormLabel>
            <Input
              bg={"gray.100"}
              border={0}
              color={"gray.500"}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />{" "}
            {errors.last_name &&
              errors.last_name.map((e) => (
                <Text key={e} color="red">
                  {e}
                </Text>
              ))}
          </FormControl>
          <FormControl mt={4} isInvalid={touched.phoneNumber && errors.phoneNumber}>
            <FormLabel>Telefon</FormLabel>
            <Input
              bg={"gray.100"}
              border={0}
              color={"gray.500"}
              value={phoneNumber}
              placeholder="5XX XXX XX XX"
              maxLength="13"
              onChange={(e) => handleFieldChange('phoneNumber', e.target.value)}
              onBlur={(e) => handleFieldBlur('phoneNumber', e.target.value)}
            />
            {touched.phoneNumber && errors.phoneNumber && (
              <FormHelperText color="red">
                {errors.phoneNumber}
              </FormHelperText>
            )}
            {errors.phone_number &&
              errors.phone_number.map((e) => (
                <Text key={e} color="red">
                  {e}
                </Text>
              ))}
          </FormControl>
          <FormControl mt={4}>
            <FormLabel>Şehir</FormLabel>
            <CitySelect
              bg={"gray.100"}
              border={0}
              color={"gray.500"}
              {...{ city, setCity }}
            />
            {errors.city &&
              errors.city.map((e) => (
                <Text key={e} color="red">
                  {e}
                </Text>
              ))}
          </FormControl>
          <FormControl mt={4}>
            <FormLabel>Adres</FormLabel>
            <Textarea
              bg={"gray.100"}
              border={0}
              color={"gray.500"}
              value={addres}
              onChange={(e) => setAddres(e.target.value)}
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button onClick={handleCreate} colorScheme="blue" mr={3}>
            Oluştur
          </Button>
          <Button onClick={disclosure.onClose}>Vazgeç</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
